import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { isoFromCountryName } from '../../lib/utils/countries';

interface AddressAutocompleteProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected?: (place: AddressDetails) => void;
  placeholder?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  country?: string; // New prop for country restriction
  /** true = input nudo senza label/icona/bordo, per l'uso dentro righe di proprietà */
  bare?: boolean;
}

export interface AddressDetails {
  street_address?: string;
  street_number?: string;
  city?: string;
  province?: string;
  region?: string;
  postal_code?: string;
  country?: string;
  formatted_address?: string;
  place_id?: string;
}

declare global {
  interface Window {
    google: any;
    initGooglePlaces: () => void;
    gm_authFailure: () => void;
  }
}

// Rifiuto della chiave Google (chiave non valida, dominio non autorizzato, es.
// localhost, o fatturazione non attiva). Google chiama window.gm_authFailure
// anche DOPO aver agganciato l'autocompletamento e in quel caso DISABILITA da
// sé l'input (grigio, icona "!", placeholder d'errore): senza intervenire il
// campo resta bloccato e l'indirizzo non si può nemmeno scrivere a mano.
// Il gestore è globale e unico: avvisa tutti i campi montati.
let googleAuthFailed = false;
const authFailureListeners = new Set<() => void>();
function installGoogleAuthFailureHandler() {
  window.gm_authFailure = () => {
    googleAuthFailed = true;
    authFailureListeners.forEach(listener => listener());
  };
}

export function AddressAutocomplete({
  label,
  value,
  onChange,
  onPlaceSelected,
  placeholder = "Inizia a digitare un indirizzo...",
  hint,
  error,
  required = false,
  disabled = false,
  country = "Italia",
  bare = false
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [googleError, setGoogleError] = useState<string | null>(null);
  // Ultimo valore controllato: serve a ripristinare l'input dopo la scelta Google
  const valueRef = useRef(value);
  valueRef.current = value;

  // Check if Google Places API is available
  const checkGoogleAvailability = () => {
    return new Promise<boolean>((resolve) => {
      // Check if Google is already loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        resolve(true);
        return;
      }

      // Chiave già rifiutata in questa sessione: niente autocompletamento, inserimento manuale
      if (googleAuthFailed) {
        resolve(false);
        return;
      }

      // Listen for other Google Maps errors
      const handleGoogleError = (error: any) => {
        console.error('Google Maps error:', error);
        if (error.message?.includes('ApiNotActivatedMapError')) {
          setGoogleError('Maps JavaScript API non attivata. Attivala su Google Cloud Console.');
        } else if (error.message?.includes('RefererNotAllowedMapError')) {
          setGoogleError('Dominio non autorizzato per questa API key');
        } else {
          setGoogleError('Errore nel caricamento Google Places');
        }
        resolve(false);
      };

      // Set up callback for when Google loads
      window.initGooglePlaces = () => {
        // Check for immediate errors
        if (window.google?.maps?.event) {
          window.google.maps.event.addDomListener(window, 'error', handleGoogleError);
        }
        resolve(true);
      };

      // Load Google Places API if not already loaded
      if (!document.querySelector('script[src*="maps.googleapis.com"]')) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_PLACES_API_KEY}&libraries=places&callback=initGooglePlaces`;
        script.async = true;
        script.defer = true;
        script.onerror = () => {
          setGoogleError('Impossibile caricare Google Places API');
          resolve(false);
        };
        document.head.appendChild(script);
      }

      // Fallback timeout
      setTimeout(() => {
        if (!window.google?.maps?.places) {
          setGoogleError('Timeout nel caricamento Google Places');
          resolve(false);
        }
      }, 10000);
    });
  };

  // Nome paese → ISO: fonte unica in lib/utils/countries.ts (tollerante a
  // maiuscole/minuscole e sinonimi come "Italy").
  const getCountryCode = (countryName: string): string =>
    isoFromCountryName(countryName) ?? '';

  const initializeAutocomplete = () => {
    if (googleAuthFailed || !inputRef.current || !window.google?.maps?.places) return;

    // Don't initialize if no country selected
    if (!country || country.trim() === '') {
      console.log('AddressAutocomplete: No country selected, skipping initialization');
      return;
    }

    const countryCode = getCountryCode(country);

    // Don't initialize if country code not found
    if (!countryCode) {
      console.warn('Country code not found for:', country);
      return;
    }

    console.log(`Initializing Google Places Autocomplete for country: ${country} (${countryCode})`);

    // Create autocomplete instance
    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      {
        types: ['address'],
        componentRestrictions: { country: countryCode }, // Use selected country
        fields: ['address_components', 'formatted_address', 'place_id', 'geometry']
      }
    );

    // Add place changed listener
    autocompleteRef.current.addListener('place_changed', () => {
      try {
        const place = autocompleteRef.current?.getPlace();

        // Check if place exists and has required data
        if (!place || !place.address_components || place.address_components.length === 0) {
          console.warn('Google Places returned incomplete data:', place);
          return;
        }

        // Parse address components
        const addressDetails: AddressDetails = {
          formatted_address: place.formatted_address || '',
          place_id: place.place_id || ''
        };

        place.address_components.forEach((component: any) => {
          if (!component || !component.types) {
            return;
          }
          const types = component.types;

        if (types.includes('street_number')) {
          addressDetails.street_number = component.long_name;
        } else if (types.includes('route')) {
          addressDetails.street_address = component.long_name;
        } else if (types.includes('locality') || types.includes('administrative_area_level_3')) {
          addressDetails.city = component.long_name;
        } else if (types.includes('administrative_area_level_2')) {
          // Map Italian province names to codes - try both long and short names
          const provinceName = component.long_name;
          const provinceShort = component.short_name;
          addressDetails.province = getProvinceCode(provinceName) || getProvinceCode(provinceShort);
        } else if (types.includes('administrative_area_level_1')) {
          // Map Italian region names to codes
          const regionName = component.long_name;
          addressDetails.region = getRegionCode(regionName);
        } else if (types.includes('postal_code')) {
          addressDetails.postal_code = component.long_name;
        } else if (types.includes('country')) {
          addressDetails.country = component.long_name;
        }
      });

        // Include the formatted address in the address details
        addressDetails.formatted_address = place.formatted_address;

        // Call the callback with parsed details - let the parent handle all updates
        if (onPlaceSelected) {
          onPlaceSelected(addressDetails);
        }

        // Google scrive nell'input il suo testo completo ("Via Roma, 12, Milano MI,
        // Italia") senza passare da React. Se il valore del padre non cambia
        // (stessa via già salvata) React non riallinea il campo e alla prima
        // correzione a mano tutto quel testo finiva nella via del cliente e poi
        // nel contratto. Si rimette nell'input il valore controllato.
        setTimeout(() => {
          const el = inputRef.current;
          if (el && el.value !== (valueRef.current ?? '')) el.value = valueRef.current ?? '';
        }, 50);
      } catch (error) {
        console.error('Error processing Google Places data:', error);
        setGoogleError('Errore nel processare i dati di Google Places');
      }
    });
  };

  // Helper function to map Italian province names to codes
  const getProvinceCode = (provinceName: string): string => {
    if (!provinceName) return '';
    
    const provinceMap: Record<string, string> = {
      // Province names (long names)
      'Agrigento': 'AG', 'Alessandria': 'AL', 'Ancona': 'AN', 'Aosta': 'AO',
      'Arezzo': 'AR', 'Ascoli Piceno': 'AP', 'Asti': 'AT', 'Avellino': 'AV',
      'Bari': 'BA', 'Barletta-Andria-Trani': 'BT', 'Belluno': 'BL', 'Benevento': 'BN',
      'Bergamo': 'BG', 'Biella': 'BI', 'Bologna': 'BO', 'Bolzano': 'BZ',
      'Brescia': 'BS', 'Brindisi': 'BR', 'Cagliari': 'CA', 'Caltanissetta': 'CL',
      'Campobasso': 'CB', 'Carbonia-Iglesias': 'CI', 'Caserta': 'CE', 'Catania': 'CT',
      'Catanzaro': 'CZ', 'Chieti': 'CH', 'Como': 'CO', 'Cosenza': 'CS',
      'Cremona': 'CR', 'Crotone': 'KR', 'Cuneo': 'CN', 'Enna': 'EN',
      'Fermo': 'FM', 'Ferrara': 'FE', 'Firenze': 'FI', 'Foggia': 'FG',
      'Forlì-Cesena': 'FC', 'Frosinone': 'FR', 'Genova': 'GE', 'Gorizia': 'GO',
      'Grosseto': 'GR', 'Imperia': 'IM', 'Isernia': 'IS', 'La Spezia': 'SP',
      'L\'Aquila': 'AQ', 'Latina': 'LT', 'Lecce': 'LE', 'Lecco': 'LC',
      'Livorno': 'LI', 'Lodi': 'LO', 'Lucca': 'LU', 'Macerata': 'MC',
      'Mantova': 'MN', 'Massa-Carrara': 'MS', 'Matera': 'MT', 'Messina': 'ME',
      'Milano': 'MI', 'Modena': 'MO', 'Monza e Brianza': 'MB', 'Napoli': 'NA',
      'Novara': 'NO', 'Nuoro': 'NU', 'Ogliastra': 'OG', 'Olbia-Tempio': 'OT',
      'Oristano': 'OR', 'Padova': 'PD', 'Palermo': 'PA', 'Parma': 'PR',
      'Pavia': 'PV', 'Perugia': 'PG', 'Pesaro e Urbino': 'PU', 'Pescara': 'PE',
      'Piacenza': 'PC', 'Pisa': 'PI', 'Pistoia': 'PT', 'Pordenone': 'PN',
      'Potenza': 'PZ', 'Prato': 'PO', 'Ragusa': 'RG', 'Ravenna': 'RA',
      'Reggio Calabria': 'RC', 'Reggio Emilia': 'RE', 'Rieti': 'RI', 'Rimini': 'RN',
      'Roma': 'RM', 'Rovigo': 'RO', 'Salerno': 'SA', 'Sassari': 'SS',
      'Savona': 'SV', 'Siena': 'SI', 'Siracusa': 'SR', 'Sondrio': 'SO',
      'Taranto': 'TA', 'Teramo': 'TE', 'Terni': 'TR', 'Torino': 'TO',
      'Trapani': 'TP', 'Trento': 'TN', 'Treviso': 'TV', 'Trieste': 'TS',
      'Udine': 'UD', 'Varese': 'VA', 'Venezia': 'VE', 'Verbano-Cusio-Ossola': 'VB',
      'Vercelli': 'VC', 'Verona': 'VR', 'Vibo Valentia': 'VV', 'Vicenza': 'VI',
      'Viterbo': 'VT',
      
      // Alternative names that Google might return
      'Metropolitan City of Milan': 'MI',
      'Metropolitan City of Rome': 'RM',
      'Metropolitan City of Naples': 'NA',
      'Metropolitan City of Turin': 'TO',
      'Metropolitan City of Bari': 'BA',
      'Metropolitan City of Florence': 'FI',
      'Metropolitan City of Bologna': 'BO',
      'Metropolitan City of Genoa': 'GE',
      'Metropolitan City of Venice': 'VE',
      'Metropolitan City of Palermo': 'PA',
      'Metropolitan City of Catania': 'CT',
      'Metropolitan City of Messina': 'ME',
      'Metropolitan City of Cagliari': 'CA',
      'Metropolitan City of Reggio Calabria': 'RC',
      
      // Short codes that might be returned
      'AG': 'AG', 'AL': 'AL', 'AN': 'AN', 'AO': 'AO', 'AR': 'AR', 'AP': 'AP',
      'AT': 'AT', 'AV': 'AV', 'BA': 'BA', 'BT': 'BT', 'BL': 'BL', 'BN': 'BN',
      'BG': 'BG', 'BI': 'BI', 'BO': 'BO', 'BZ': 'BZ', 'BS': 'BS', 'BR': 'BR',
      'CA': 'CA', 'CL': 'CL', 'CB': 'CB', 'CI': 'CI', 'CE': 'CE', 'CT': 'CT',
      'CZ': 'CZ', 'CH': 'CH', 'CO': 'CO', 'CS': 'CS', 'CR': 'CR', 'KR': 'KR',
      'CN': 'CN', 'EN': 'EN', 'FM': 'FM', 'FE': 'FE', 'FI': 'FI', 'FG': 'FG',
      'FC': 'FC', 'FR': 'FR', 'GE': 'GE', 'GO': 'GO', 'GR': 'GR', 'IM': 'IM',
      'IS': 'IS', 'SP': 'SP', 'AQ': 'AQ', 'LT': 'LT', 'LE': 'LE', 'LC': 'LC',
      'LI': 'LI', 'LO': 'LO', 'LU': 'LU', 'MC': 'MC', 'MN': 'MN', 'MS': 'MS',
      'MT': 'MT', 'ME': 'ME', 'MI': 'MI', 'MO': 'MO', 'MB': 'MB', 'NA': 'NA',
      'NO': 'NO', 'NU': 'NU', 'OG': 'OG', 'OT': 'OT', 'OR': 'OR', 'PD': 'PD',
      'PA': 'PA', 'PR': 'PR', 'PV': 'PV', 'PG': 'PG', 'PU': 'PU', 'PE': 'PE',
      'PC': 'PC', 'PI': 'PI', 'PT': 'PT', 'PN': 'PN', 'PZ': 'PZ', 'PO': 'PO',
      'RG': 'RG', 'RA': 'RA', 'RC': 'RC', 'RE': 'RE', 'RI': 'RI', 'RN': 'RN',
      'RM': 'RM', 'RO': 'RO', 'SA': 'SA', 'SS': 'SS', 'SV': 'SV', 'SI': 'SI',
      'SR': 'SR', 'SO': 'SO', 'TA': 'TA', 'TE': 'TE', 'TR': 'TR', 'TO': 'TO',
      'TP': 'TP', 'TN': 'TN', 'TV': 'TV', 'TS': 'TS', 'UD': 'UD', 'VA': 'VA',
      'VE': 'VE', 'VB': 'VB', 'VC': 'VC', 'VR': 'VR', 'VV': 'VV', 'VI': 'VI',
      'VT': 'VT'
    };

    return provinceMap[provinceName] || '';
  };

  // Helper function to map Italian region names to codes
  const getRegionCode = (regionName: string): string => {
    if (!regionName) return '';
    
    const regionMap: Record<string, string> = {
      'Abruzzo': 'ABR',
      'Basilicata': 'BAS',
      'Calabria': 'CAL',
      'Campania': 'CAM',
      'Emilia-Romagna': 'EMR',
      'Friuli-Venezia Giulia': 'FVG',
      'Lazio': 'LAZ',
      'Liguria': 'LIG',
      'Lombardia': 'LOM',
      'Marche': 'MAR',
      'Molise': 'MOL',
      'Piemonte': 'PIE',
      'Puglia': 'PUG',
      'Sardegna': 'SAR',
      'Sicilia': 'SIC',
      'Toscana': 'TOS',
      'Trentino-Alto Adige': 'TAA',
      'Umbria': 'UMB',
      'Valle d\'Aosta': 'VDA',
      'Veneto': 'VEN',
      
      // Alternative names that Google might return
      'Abruzzo Region': 'ABR',
      'Basilicata Region': 'BAS',
      'Calabria Region': 'CAL',
      'Campania Region': 'CAM',
      'Emilia-Romagna Region': 'EMR',
      'Friuli Venezia Giulia': 'FVG',
      'Lazio Region': 'LAZ',
      'Liguria Region': 'LIG',
      'Lombardy': 'LOM',
      'Marche Region': 'MAR',
      'Molise Region': 'MOL',
      'Piedmont': 'PIE',
      'Apulia': 'PUG',
      'Sardinia': 'SAR',
      'Sicily': 'SIC',
      'Tuscany': 'TOS',
      'Trentino-South Tyrol': 'TAA',
      'Umbria Region': 'UMB',
      'Aosta Valley': 'VDA',
      'Veneto Region': 'VEN'
    };

    return regionMap[regionName] || '';
  };

  // Chiave rifiutata (anche dopo l'aggancio): stacca l'autocompletamento e
  // riabilita l'input che Google ha disattivato, così si scrive a mano
  useEffect(() => {
    installGoogleAuthFailureHandler();
    const recover = () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      const el = inputRef.current;
      if (el) {
        el.disabled = false;
        el.classList.remove('gm-err-autocomplete');
        el.style.backgroundImage = '';
        el.placeholder = placeholder;
      }
      setIsGoogleLoaded(false);
      setIsLoading(false);
      setGoogleError('API key non valida o non autorizzata');
    };
    authFailureListeners.add(recover);
    if (googleAuthFailed) recover();
    return () => { authFailureListeners.delete(recover); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadGooglePlaces = async () => {
      setIsLoading(true);
      const loaded = await checkGoogleAvailability();
      setIsGoogleLoaded(loaded);
      setIsLoading(false);
      
      if (loaded) {
        // Small delay to ensure DOM is ready
        setTimeout(initializeAutocomplete, 100);
      }
    };

    loadGooglePlaces();

    // Cleanup
    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
      }
    };
  }, []);

  // Re-initialize if Google becomes available later
  useEffect(() => {
    if (isGoogleLoaded && inputRef.current && !autocompleteRef.current) {
      initializeAutocomplete();
    }
  }, [isGoogleLoaded]);

  // Re-initialize when country changes
  useEffect(() => {
    console.log(`AddressAutocomplete: Country changed to "${country}", isGoogleLoaded: ${isGoogleLoaded}`);

    if (isGoogleLoaded && inputRef.current) {
      console.log('AddressAutocomplete: Re-initializing autocomplete for country change');

      // Clean up existing autocomplete if it exists
      if (autocompleteRef.current) {
        console.log('AddressAutocomplete: Cleaning up existing autocomplete');
        window.google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
        autocompleteRef.current = null;
      }

      // Initialize new autocomplete with updated country
      // Small delay to ensure cleanup is complete
      setTimeout(() => {
        console.log('AddressAutocomplete: Calling initializeAutocomplete after delay');
        initializeAutocomplete();
      }, 150);
    }
  }, [country, isGoogleLoaded]);

  return (
    <div>
      {!bare && (
        <label className="block text-sm font-medium text-black mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className={bare ? 'relative' : 'relative rounded-md shadow-sm'}>
        <div className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${bare ? 'hidden' : ''}`}>
          {isLoading ? (
            <Loader2 className="h-5 w-5 text-black/40 animate-spin" />
          ) : (
            <MapPin className="h-5 w-5 text-black/40" />
          )}
        </div>
        <input
          ref={inputRef}
          type="text"
          required={required}
          disabled={disabled || isLoading || !country || country.trim() === ''}
          className={bare
            ? 'block w-full bg-transparent text-[13px] text-[#37352F] dark:text-white placeholder-[rgba(55,53,47,0.4)] dark:placeholder-gray-500 focus:outline-none disabled:cursor-not-allowed'
            : `block w-full pl-10 pr-3 py-2.5 glass-card border rounded-lg shadow-sm focus:ring-2 focus:border-transparent placeholder-gray-500 dark:placeholder-white/50 ${
            error
              ? 'border-red-400/30 text-gray-900 dark:text-white focus:ring-red-500 focus:border-red-500'
              : 'border-black/10 dark:border-gray-700 text-gray-900 dark:text-white focus:ring-blue-500'
          } ${disabled || isLoading || !country || country.trim() === '' ? 'bg-gray-900/50 cursor-not-allowed' : ''}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={
            isLoading
              ? "Caricamento Google Places..."
              : !country || country.trim() === ''
              ? "Seleziona prima un paese per abilitare l'autocompletamento"
              : placeholder
          }
        />
      </div>
      {!isGoogleLoaded && !isLoading && (
        <div className="mt-1">
          {googleError ? (
            <p className={bare ? 'text-[11px] text-[rgba(55,53,47,0.5)] dark:text-gray-400' : 'text-sm text-red-600'}>
              <span className="font-medium">Autocompletamento non disponibile</span> ({googleError}): inserisci l'indirizzo a mano.
            </p>
          ) : (
            <p className="text-sm text-amber-600">
              Google Places non disponibile. Inserisci l'indirizzo manualmente.
            </p>
          )}
        </div>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
      {!error && hint && (
        <p className="mt-1 text-sm text-black/60">{hint}</p>
      )}
    </div>
  );
}